const isPlainObject = (value) =>
  value !== null &&
  typeof value === "object" &&
  !Array.isArray(value);

const mergeValuesIfObject = (existing, incoming) => {
  if (isPlainObject(existing) && isPlainObject(incoming)) {
    return { ...existing, ...incoming };
  }
  return incoming;
};

const applyUpdate = (source, fieldName, value) => {
  if (source == null) {
    return {
      handled: true,
      updated: true,
      result: { [fieldName]: value },
    };
  }

  if (Array.isArray(source)) {
    let handled = false;
    let updated = false;
    const result = source.map((item) => {
      if (handled) {
        return item;
      }
      if (isPlainObject(item) || Array.isArray(item)) {
        const outcome = applyUpdate(item, fieldName, value);
        if (outcome.handled) {
          handled = true;
          if (outcome.updated) {
            updated = true;
            return outcome.result;
          }
        }
      }
      return item;
    });

    if (!handled) {
      return { handled: false, updated: false, result: source };
    }

    return {
      handled: true,
      updated,
      result: updated ? result : source,
    };
  }

  if (!isPlainObject(source)) {
    return { handled: false, updated: false, result: source };
  }

  if (Object.prototype.hasOwnProperty.call(source, fieldName)) {
    const existingValue = source[fieldName];
    const nextValue = mergeValuesIfObject(existingValue, value);
    if (existingValue === nextValue) {
      return { handled: true, updated: false, result: source };
    }
    return {
      handled: true,
      updated: true,
      result: {
        ...source,
        [fieldName]: nextValue,
      },
    };
  }

  for (const [key, child] of Object.entries(source)) {
    if (!isPlainObject(child) && !Array.isArray(child)) {
      continue;
    }

    const outcome = applyUpdate(child, fieldName, value);
    if (outcome.handled) {
      if (!outcome.updated) {
        return { handled: true, updated: false, result: source };
      }
      return {
        handled: true,
        updated: true,
        result: {
          ...source,
          [key]: outcome.result,
        },
      };
    }
  }

  return { handled: false, updated: false, result: source };
};

/**
 * Update a nested object while preserving immutability.
 * Finds the first occurrence of `fieldName` within the object tree and
 * replaces/merges it with `value`. If the field is not found, it will be
 * added at the current level for backward compatibility.
 *
 * @param {object|array|null} source - Existing nested values
 * @param {string} fieldName - Target field name to update
 * @param {*} value - New value to set for the field
 * @returns {object|array} - Updated structure (reuses original reference when unchanged)
 */
export function updateFlattenedNestedValue(source, fieldName, value) {
  const { result, handled, updated } = applyUpdate(source, fieldName, value);

  if (handled) {
    return updated ? result : source;
  }

  if (source == null) {
    return { [fieldName]: value };
  }

  if (!isPlainObject(source)) {
    return source;
  }

  if (Object.prototype.hasOwnProperty.call(source, fieldName) && source[fieldName] === value) {
    return source;
  }

  return {
    ...source,
    [fieldName]: value,
  };
}

export { isPlainObject };

